import type { ErpPropertyOption } from "@forgepoint/shared";
import type { MappingSchema } from "./customFieldMappingHelpers";

/** The Quoted By mapping rows read from the resolved schema. */
export interface QuotedByMappingOptions {
  userIds: string[];
  userLabels: Record<string, string>;
  employeeOptions: ErpPropertyOption[];
}

const EMPTY_OPTIONS: QuotedByMappingOptions = {
  userIds: [],
  userLabels: {},
  employeeOptions: [],
};

/**
 * Reads the Quoted By users, in the schema's property order, and the employee
 * options every user shares from the resolved schema's `enum`/`enumNames`.
 * @param mappingSchema - The resolved `quotedByEmployeeByUserId` sub-schema
 * @returns The user ids, their display names, and the employee options
 */
export function getQuotedByMappingOptions(
  mappingSchema: MappingSchema | null,
): QuotedByMappingOptions {
  const properties = mappingSchema?.properties;
  if (!properties) {
    return EMPTY_OPTIONS;
  }
  const propertyOrder = Array.isArray(mappingSchema.propertyOrder)
    ? mappingSchema.propertyOrder
    : Object.keys(properties);
  const users = propertyOrder.flatMap((userId) => {
    const property = properties[userId];
    if (property === undefined || typeof property === "boolean") {
      return [];
    }
    return [{ userId, property }];
  });
  if (users.length === 0) {
    return EMPTY_OPTIONS;
  }
  return {
    userIds: users.map(({ userId }) => userId),
    userLabels: Object.fromEntries(
      users.map(({ userId, property }) => [userId, property.title ?? userId]),
    ),
    employeeOptions: toEmployeeOptions(
      users[0].property.enum,
      users[0].property.enumNames,
    ),
  };
}

/**
 * Pairs schema enum values with their `enumNames` labels, skipping non-string values.
 * @param values - The schema `enum` values
 * @param labels - The schema `enumNames` labels
 * @returns The employee options, labelled by value when no label is given
 */
function toEmployeeOptions(
  values: unknown,
  labels: unknown,
): ErpPropertyOption[] {
  if (!Array.isArray(values)) {
    return [];
  }
  const labelList: unknown[] = Array.isArray(labels) ? labels : [];
  return values.flatMap((value, index) => {
    if (typeof value !== "string") {
      return [];
    }
    const label = labelList[index];
    return [{ value, label: typeof label === "string" ? label : value }];
  });
}
